import { A } from "@solidjs/router";
import { type Component, For, Index, type JSX, onMount } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "@/components/ui/skeleton";
import { usePostHog } from "@/hooks/usePostHog";

const UPGRADE_HREF = "/profile/company/upgrade";

/**
 * Content-scoped state surfaces for the Talent Pool comparison tab. They mirror
 * the list-page state language (`pool-page-states.tsx`) but live INSIDE the tab
 * region (no full-page `min-h-[60vh]` shell) so the segmented control + Ricerca
 * tab stay visible and interactive throughout (AC8/AC10 content-scoped gating).
 */

const SKELETON_ROWS = [0, 1, 2] as const;

/**
 * Neutral loading — shown while capabilities are unresolved (never the upsell —
 * D16 no-flash) and while the match query's first page loads.
 */
export const MatchTabLoading: Component = () => (
  <div
    class="flex w-full flex-col gap-4 py-4"
    aria-busy="true"
    aria-live="polite"
  >
    <Index each={SKELETON_ROWS}>
      {() => (
        <div class="flex flex-col gap-3 rounded-lg border border-border p-4">
          <div class="flex items-center gap-3">
            <LoadingSkeleton class="h-12 w-12 rounded-full" />
            <div class="flex flex-1 flex-col gap-2">
              <LoadingSkeleton class="h-4 w-1/3 rounded-md" />
              <LoadingSkeleton class="h-3 w-1/4 rounded-md" />
            </div>
          </div>
          <LoadingSkeleton class="h-3 w-full rounded-md" />
          <LoadingSkeleton class="h-3 w-2/3 rounded-md" />
        </div>
      )}
    </Index>
  </div>
);

export type MatchTabUpsellProps = {
  /** Current plan slug, attached to the impression event. */
  plan: string;
};

/**
 * Content-scoped upsell shown when the plan does not include the Talent Pool
 * (AC8). Reuses the list upsell copy (`company.talent_pool.list.upsell.*`) so the
 * save-flow, list, and match upsells read as one system. Fires one impression on
 * mount with the NEW `source: "match_tab"`, and records CTA intent — navigation
 * still proceeds via the `<A>` link.
 */
export const MatchTabUpsell: Component<MatchTabUpsellProps> = (props) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  const benefits = () => [
    t("company.talent_pool.list.upsell.benefit_save"),
    t("company.talent_pool.list.upsell.benefit_compare"),
    t("company.talent_pool.list.upsell.benefit_share"),
  ];

  onMount(() => {
    capture("view_talent_pool_upsell", {
      source: "match_tab",
      plan: props.plan,
    });
  });

  const handleCtaClick = () => {
    capture("click_talent_pool_upsell_cta", { source: "match_tab" });
  };

  return (
    <div class="flex w-full justify-center px-4 py-12">
      <div class="w-full max-w-md overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
        <div class="flex flex-col gap-3 border-b border-border bg-linear-to-br from-primary/5 to-card p-6">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <i class="ph-fill ph-lock-simple text-2xl" aria-hidden="true" />
          </div>
          <h2 class="text-xl font-display font-bold text-foreground">
            {t("company.talent_pool.list.upsell.title")}
          </h2>
        </div>

        <div class="flex flex-col gap-5 p-6">
          <p class="text-sm leading-relaxed text-muted-foreground">
            {t("company.talent_pool.list.upsell.value")}
          </p>

          <ul class="flex flex-col gap-2.5 text-sm text-gray-700">
            <For each={benefits()}>
              {(benefit) => (
                <li class="flex items-center gap-2.5">
                  <i
                    class="ph-fill ph-check-circle shrink-0 text-base text-green-600"
                    aria-hidden="true"
                  />
                  <span>{benefit}</span>
                </li>
              )}
            </For>
          </ul>

          <div class="flex flex-col gap-2">
            <Button
              as={A}
              href={UPGRADE_HREF}
              onClick={handleCtaClick}
              size="lg"
              class="w-full"
            >
              {t("company.talent_pool.list.upsell.cta_primary")}
            </Button>
            <Button
              as={A}
              href={UPGRADE_HREF}
              onClick={handleCtaClick}
              variant="ghost"
              class="w-full text-muted-foreground hover:text-foreground"
            >
              {t("company.talent_pool.list.upsell.cta_secondary")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Shared centered container for the content-scoped message states. */
const MatchStateShell: Component<{ children: JSX.Element }> = (props) => (
  <div class="flex w-full flex-col items-center justify-center gap-6 px-6 py-16 text-center">
    {props.children}
  </div>
);

/**
 * Permission-denied (AC10 / FLOW error path 2): the plan includes the pool but
 * the caller is not an active publisher. Distinct from the upsell — a shield
 * glyph, permission framing, and NO upgrade CTA (upgrading grants no role).
 * Reuses the list copy. No tracking.
 */
export const MatchTabPermissionDenied: Component = () => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <i
          class="ph ph-shield-warning text-3xl text-muted-foreground"
          aria-hidden="true"
        />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t("company.talent_pool.list.permission_denied.title")}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t("company.talent_pool.list.permission_denied.description")}
        </p>
      </div>
    </MatchStateShell>
  );
};

export type MatchTabInvalidOfferProps = {
  onBackToSearch: () => void;
};

/**
 * Invalid-offer (AC10 / FLOW error path 3): the `job_offer_id` is missing or
 * belongs to another workspace (404, indistinguishable by design). CTA returns
 * to the search/offers surface.
 */
export const MatchTabInvalidOffer: Component<MatchTabInvalidOfferProps> = (
  props,
) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <i
          class="ph ph-file-x text-3xl text-muted-foreground"
          aria-hidden="true"
        />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t("company.talent_pool.match.invalid_offer.title")}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t("company.talent_pool.match.invalid_offer.description")}
        </p>
      </div>
      <Button variant="outline" onClick={() => props.onBackToSearch()}>
        {t("company.talent_pool.match.invalid_offer.cta")}
      </Button>
    </MatchStateShell>
  );
};

export type MatchTabErrorProps = {
  onRetry: () => void;
};

/**
 * Generic retryable error (AC10 / FLOW error path 4): capabilities or match
 * fetch failed (500 / network). "Riprova" re-issues the request; the Ricerca tab
 * stays fully usable. Reuses the list error copy. No tracking.
 */
export const MatchTabError: Component<MatchTabErrorProps> = (props) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
        <i
          class="ph ph-warning-circle text-3xl text-destructive"
          aria-hidden="true"
        />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t("company.talent_pool.list.error_title")}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t("company.talent_pool.list.error_description")}
        </p>
      </div>
      <Button variant="outline" onClick={() => props.onRetry()}>
        {t("company.talent_pool.list.retry")}
      </Button>
    </MatchStateShell>
  );
};

export const MatchTabRateLimited: Component<{
  retryAfterSeconds?: number;
}> = (props) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <i class="ph ph-clock text-3xl text-amber-700" aria-hidden="true" />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t(
            "company.search.freelancers.result.semantic_errors.rate_limited_title",
          )}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {props.retryAfterSeconds
            ? t(
                "company.search.freelancers.result.semantic_errors.rate_limited_with_retry",
                { seconds: props.retryAfterSeconds },
              )
            : t(
                "company.search.freelancers.result.semantic_errors.rate_limited_description",
              )}
        </p>
      </div>
    </MatchStateShell>
  );
};

export const MatchTabSemanticUnavailable: Component<MatchTabErrorProps> = (
  props,
) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <i class="ph ph-cloud-x text-3xl text-amber-700" aria-hidden="true" />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t(
            "company.search.freelancers.result.semantic_errors.unavailable_title",
          )}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t(
            "company.search.freelancers.result.semantic_errors.unavailable_description",
          )}
        </p>
      </div>
      <Button variant="outline" onClick={() => props.onRetry()}>
        {t("company.search.freelancers.result.semantic_errors.retry")}
      </Button>
    </MatchStateShell>
  );
};

export type MatchEmptyPoolProps = {
  /** Switch to the Ricerca tab so the user can start saving talents. */
  onGoToSearchTab: () => void;
};

/**
 * Empty pool with an ENABLED plan and no active filters (AC9): the workspace has
 * saved nothing yet. A positive, inviting state (never a failure) — the most
 * common state for the Small business persona. CTA switches to the Ricerca tab.
 */
export const MatchEmptyPool: Component<MatchEmptyPoolProps> = (props) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <i
          class="ph ph-bookmark-simple text-3xl text-muted-foreground"
          aria-hidden="true"
        />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t("company.talent_pool.match.empty_pool.title")}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t("company.talent_pool.match.empty_pool.description")}
        </p>
      </div>
      <Button variant="outline" onClick={() => props.onGoToSearchTab()}>
        {t("company.talent_pool.match.empty_pool.cta")}
      </Button>
    </MatchStateShell>
  );
};

export type MatchEmptyFilteredProps = {
  /** Reset the shared filters (also clears the Ricerca tab). */
  onResetFilters: () => void;
};

/**
 * Non-empty pool, but the active filters match nothing (AC9) — distinct from
 * `empty_pool`. CTA clears the shared filters.
 */
export const MatchEmptyFiltered: Component<MatchEmptyFilteredProps> = (
  props,
) => {
  const { t } = useI18n();
  return (
    <MatchStateShell>
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <i
          class="ph ph-funnel text-3xl text-muted-foreground"
          aria-hidden="true"
        />
      </div>
      <div class="flex max-w-md flex-col gap-3">
        <h2 class="text-xl font-display font-semibold text-foreground">
          {t("company.talent_pool.match.empty_filtered.title")}
        </h2>
        <p class="text-sm leading-relaxed text-muted-foreground">
          {t("company.talent_pool.match.empty_filtered.description")}
        </p>
      </div>
      <Button variant="outline" onClick={() => props.onResetFilters()}>
        {t("company.talent_pool.match.empty_filtered.reset")}
      </Button>
    </MatchStateShell>
  );
};
